import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { List } from 'immutable';
import {
    getCustomFieldsForTeamCustom,
    wallboardTypes,
} from 'flow/utils/wallboard-utils';
import { fetchExtensions } from 'flow/actions/extension-actions';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import useAppContext from 'flow/hooks/useAppContext';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import DashboardSectionHeader from 'flow/components/dashboard/DashboardSectionHeader';
import DashboardSettings from 'flow/components/dashboard/DashboardSettings';
import AgentsSortableDeletable from './creation/team-custom/AgentsSortableDeletable';
import AddAgentsModal from './creation/team-custom/AddAgentsModal';

interface Props {
    onWallboardDataChanged: Function;
    wallboardData: any;
    maxSelectionAmount: number;
    onNavigateToCustomizeSettings: () => void;
    onNavigateToVisibilitySettings: () => void;
    userHasOmniQueue?: boolean;
}
const DashboardTeamCustom = ({
    onWallboardDataChanged,
    wallboardData,
    maxSelectionAmount,
    onNavigateToCustomizeSettings,
    onNavigateToVisibilitySettings,
    userHasOmniQueue,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [agentsModalOpen, setAgentsModalOpen] = useState(false);

    const customFields = useMemo(() => {
        return getCustomFieldsForTeamCustom(i18next, userHasOmniQueue);
    }, [i18next, userHasOmniQueue]);

    useEffect(() => {
        dispatch(fetchExtensions('contact', 'live'));
    }, [dispatch]);

    const wallboardDescriptionChanged = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const description = event.target.value;
        const wallboardDataUpdated = wallboardData.set('name', description);

        onWallboardDataChanged(wallboardDataUpdated);
    };

    const onAgentsSortedOrDeleted = (agents: List<ExtensionKey>) => {
        onWallboardDataChanged(wallboardData.set('agentKeys', agents));
    };

    const onAgentsAdded = (agentsAdded: List<ExtensionKey>) => {
        const existingAgents = wallboardData.get('agentKeys') || List();
        const agentsUpdated = existingAgents.concat(agentsAdded).toList();
        onWallboardDataChanged(wallboardData.set('agentKeys', agentsUpdated));
    };

    const getCustomFields = () => {
        return (
            <div className="flex flex-col px-3">
                {customFields.map(
                    (field: TypedMap<CustomField>, index: number) => (
                        <div key={index}>
                            <span className="mb-2 block text-sm text-gray-800">
                                {field.get('value')}
                            </span>
                            <span className="mb-5 block text-xs text-gray-500">
                                {field.get('description')}
                            </span>
                        </div>
                    ),
                )}
            </div>
        );
    };

    return (
        <>
            <div className="my-9">
                <DashboardSectionHeader
                    boardType={wallboardTypes.TEAM_CUSTOM}
                    userDescription={wallboardData.get('name') || ''}
                    onDescriptionChanged={wallboardDescriptionChanged}
                    setFocusOnNameField={true}
                />
            </div>
            <>
                <div className="mt-3 mb-1.5 ps-3 text-xs font-bold">
                    {i18next.t('wallboard.agents.title')}
                </div>
                <Divider className="mt-1 mb-5" />
                <div className="px-3">
                    <AgentsSortableDeletable
                        agents={wallboardData.get('agentKeys')}
                        onUpdate={onAgentsSortedOrDeleted}
                    />
                </div>
                <Divider className="mt-5 mb-3" />
                <div className="ps-3">
                    <Button
                        onClick={() => setAgentsModalOpen(true)}
                        color="primary"
                        iconKey="add"
                        className="py-1"
                    >
                        {i18next.t('user_picker.add_users')}
                    </Button>
                </div>
                {agentsModalOpen ? (
                    <AddAgentsModal
                        agentsToExclude={wallboardData.get('agentKeys')}
                        onClickAdd={onAgentsAdded}
                        onClose={() => setAgentsModalOpen(false)}
                        maximumAmount={
                            maxSelectionAmount -
                            (wallboardData.get('agentKeys')?.size || 0)
                        }
                    />
                ) : null}
            </>
            <div className="mt-12 mb-1.5 px-3 text-xs font-bold">
                {i18next.t('wallboard.custom_fields.title')}
            </div>
            <Divider className="mt-1 mb-5" />
            {getCustomFields()}
            <Divider className="mt-1 mb-9" />
            <div className="mb-9">
                <DashboardSettings
                    wallboardSettings={wallboardData.get('settings')}
                    onNavigateToCustomizeSettings={
                        onNavigateToCustomizeSettings
                    }
                    onNavigateToVisibilitySettings={
                        onNavigateToVisibilitySettings
                    }
                    boardType={wallboardTypes.TEAM_CUSTOM}
                />
            </div>
        </>
    );
};

export default DashboardTeamCustom;

interface CustomField {
    key: string;
    value: string;
    description: string;
    selected: boolean;
}
